<template>
  <vs-card>
    <div class="flex items-center justify-between mb-4 mt-2">
      <h4 class="mb-6 sm:ml-5">Timing of treatment for {{ dataToEdit.name }}</h4>
      <vs-button type="border" class="p-4 py-3" @click="showLogs = !showLogs">View Logs</vs-button>
    </div>
    <form autocomplete="off" @submit.prevent class="space-y-4">
      <vs-row vs-align="flex-start" vs-type="flex" vs-justify="left" vs-w="12">
        <vs-col vs-type="flex" vs-justify="left" vs-align="left" vs-lg="6" vs-sm="6" vs-xs="12">
          <div class="w-full sm:ml-5">
            <label class="label">Timing of Treatment</label>
            <vs-textarea
              :success="
                !errors.first('basic.treatmentTiming') && dataToSave.treatmentTiming != ''
              "
              val-icon-success="done"
              val-icon-danger="error"
              :danger="errors.first('basic.treatmentTiming') ? true : false"
              v-validate="'required'"
              name="treatmentTiming"
              data-vv-as="Treatment Timing"
              class="w-full"
              data-vv-scope="basic"
              :danger-text="errors.first('basic.treatmentTiming')"
              v-model="dataToSave.treatmentTiming"
              style="margin-bottom: 5px !important"
            />
            <span class="text-danger text-sm">
              {{ errors.first("basic.treatmentTiming") }}
            </span>
          </div>
        </vs-col>
      </vs-row>
      <vs-row vs-align="flex-start" vs-type="flex" vs-justify="left" vs-w="12" v-if="fieldVisibility">
        <vs-col vs-type="flex" vs-justify="left" vs-align="left" vs-lg="6" vs-sm="6" vs-xs="12">
          <div class="w-full sm:ml-5">
            <vs-input
              v-validate="{
                required: true,
                min_value: 0,
              }"
              name="doctorFee"
              label="Doctor Treatment Consultation Fee (default is $15.00)"
              icon-no-border
              class="w-full"
              v-model="dataToSave.doctorFee"
              type="number"
              icon="attach_money"
              data-vv-scope="basic"
              data-vv-as="Doctor Treatment Consultation Fee"
              :success="
                !errors.first('basic.doctorFee') && dataToSave.doctorFee != ''
              "
              val-icon-success="done"
              val-icon-danger="error"
              :danger-text="errors.first('basic.doctorFee')"
              :danger="errors.first('basic.doctorFee') ? true : false"
            />
          </div>
        </vs-col>
      </vs-row>
      <vs-row vs-align="flex-start" vs-type="flex" vs-justify="left" vs-w="12" v-if="fieldVisibility">
        <vs-col vs-type="flex" vs-justify="left" vs-align="left" vs-lg="6" vs-sm="6" vs-xs="12">
          <div class="w-full sm:ml-5">
            <vs-input
              v-validate="{
                required: true,
                min_value: 0,
              }"
              name="freshFee"
              label="Fresh Clinics Consultation Fee"
              icon-no-border
              class="w-full"
              v-model="dataToSave.freshConsultationFee"
              type="number"
              icon="attach_money"
              data-vv-scope="basic"
              data-vv-as="Fresh Clinics Consultation Fee"
              :success="
                !errors.first('basic.freshFee') && dataToSave.freshConsultationFee != ''
              "
              val-icon-success="done"
              val-icon-danger="error"
              :danger-text="errors.first('basic.freshFee')"
              :danger="errors.first('basic.freshFee') ? true : false"
            />
          </div>
        </vs-col>
      </vs-row>
      
      <!-- Price Tiers Section -->
      <div class="w-full sm:ml-5 mt-6" v-if="fieldVisibility">
        <h4 class="mb-3 font-semibold">Subscription Tier Pricing</h4>
        <p class="mb-4 text-xs text-gray-500">
          Configure pricing for different subscription tiers. These rates will override the default rates when a clinic has an active subscription.
        </p>
        
        <div v-if="subscriptionTiers.length > 0" class="space-y-3">
          <div 
            v-for="tier in subscriptionTiers" 
            :key="tier._id"
            class="flex items-center gap-4 p-4 border rounded"
            style="border-color: #e0e0e0; background-color: #fafafa;"
          >
            <div class="flex-1 min-w-0">
              <label class="block text-sm font-medium mb-1" style="color: #626262;">
                {{ tier.name }}
              </label>
            </div>
            <div class="w-40">
              <label class="block text-xs mb-1" style="color: #8a8a8a;">Doctor Rate</label>
              <vs-input
                :value="getPriceTierValue(tier._id, 'doctorRate')"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                class="w-full"
                @input="updatePriceTierValue(tier._id, 'doctorRate', $event)"
              />
            </div>
            <div class="w-40">
              <label class="block text-xs mb-1" style="color: #8a8a8a;">Fresh Clinics Rate</label>
              <vs-input
                :value="getPriceTierValue(tier._id, 'freshclinicsRate')"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                class="w-full"
                @input="updatePriceTierValue(tier._id, 'freshclinicsRate', $event)"
              />
            </div>
          </div>
        </div>
        
        <div v-else class="p-4 border rounded text-center" style="border-color: #e0e0e0; background-color: #fafafa;">
          <p class="text-sm text-gray-500">No subscription tiers available. Please create subscription tiers first.</p>
        </div>
      </div>
      
      <!-- Start Toggle Switch -->
      <div class="w-full sm:ml-5">
        <div v-if="$isAuRegion()" class="flex items-center gap-3 mb-4">
          <vs-switch 
            id="isHealthTreatment"
            v-model="isHealthTreatment"
          />
          <label for="isHealthTreatment">Fresh Health Treatment</label>
        </div>
        <div v-if="$isAuRegion()" class="flex items-center gap-3 mb-4">
          <vs-switch 
            id="allowDrugbookVisibility"
            v-model="allowDrugbookVisibility"
          />
          <label for="allowDrugbookVisibility">Allow drugbook visibility</label>
        </div>
        <div v-if="$isAuRegion()" class="flex items-center gap-3 mb-4">
          <vs-switch 
            id="allowAftercareDocsAvailability"
            v-model="allowAftercareDocsAvailability"
          />
          <label for="allowAftercareDocsAvailability">Allow aftercare document availability</label>
        </div>
        <div class="flex items-center gap-3 mb-4">
          <vs-switch 
            id="allowEnterUnit"
            v-model="allowEnterUnit"
          />
          <label for="allowEnterUnit">Allow enter unit</label>
        </div>
        <div class="flex items-center gap-3 mb-4">
          <vs-switch 
            id="allowReuseTreatment"
            v-model="allowReuseTreatment"
          />
          <label for="allowReuseTreatment">Allow re-use treatment</label>
        </div>
        <div v-if="$isAuRegion()" class="flex items-center gap-3 mb-4">
          <vs-switch 
            id="isStandAloneTreatment"
            v-model="isStandAloneTreatment"
          />
          <label for="isStandAloneTreatment">This is a standalone treatment</label>
        </div>
        <div v-if="$isAuRegion()" class="flex items-center gap-3 mb-4">
          <vs-switch 
            id="shouldRecordDiscardedAmountInDrugbook"
            v-model="shouldRecordDiscardedAmountInDrugbook"
          />
          <label for="shouldRecordDiscardedAmountInDrugbook">Record Discard amount in Drugbook</label>
        </div>
      </div>
      <!-- End Toggle Switch -->

      <vs-row vs-align="flex-start" vs-type="flex" vs-justify="left" vs-w="12">
        <vs-col vs-type="flex" vs-align="left" vs-w="6">
          <div class="w-full ml-5 mt-5">
            <vs-button size="normal" icon-pack="feather" class="mr-2" @click="submit"
              >Submit</vs-button
            >
          </div>
        </vs-col>
      </vs-row>
    </form>
    <vs-sidebar id="customSidebar" position-right parent="body" default-index="0" color="primary" class="sidebarx overflow-auto" spacer v-model="showLogs">
      <div class="w-full bg-white pb-8" slot="header">
        <h4 class="text-3xl text-primary2">
          Activity
        </h4>
      </div>
      <div v-if="history.length">
        <div v-for="(value, index) in history" :key="value">
          <vs-sidebar-item :index="(index+1)" class="static-item">
            <span v-html="value"></span>
          </vs-sidebar-item>
          <vs-divider />
        </div>
      </div>
      <div v-else>
        <span class="p-5">No logs found.</span>
      </div>
    </vs-sidebar>
  </vs-card>
</template>

<script>
import { Validator } from 'vee-validate';
import { mapActions } from 'vuex';
const dict = {
  custom: {
    doctorFee: {
      required: 'Please enter the doctor\'s fee',
      min_value: 'Doctor\'s fee must be at least $1',
    },
    freshFee: {
      required: 'Please enter the fresh consultation fee',
      min_value: 'The fresh consultation fee must be at least $1',
    },
  },
};
Validator.localize('en', dict);
export default {
  name: 'TreatmentTypesEdit',
  data() {
    return {
      dataToEdit: undefined,
      dataToSave: {
        treatmentTiming: '',
        doctorFee: 0,
        freshConsultationFee: 0,
      },
      routeType: undefined,
      fieldVisibility: false,
      allowDrugbookVisibility: false,
      allowAftercareDocsAvailability: false,
      allowEnterUnit: false,
      allowReuseTreatment: false,
      isStandAloneTreatment: false,
      isHealthTreatment: false,
      shouldRecordDiscardedAmountInDrugbook: false,
      history: [],
      showLogs: false,
      priceTiers: [],
      subscriptionTiers: [],
      priceTierData: {},
    };
  },
  methods: {
    ...mapActions('treatmentTypes', ['treatmentTypesById', 'saveTreatmentTiming']),
    ...mapActions('admin', ['getSettingsByKey']),
    ...mapActions('subscriptionTiers', ['fetchSubscriptionTiers']),
    async fetchById(id) {
      this.$vs.loading();
      try {
        const res = await this.treatmentTypesById({ id });
        this.dataToEdit = res.data.data;
        this.dataToSave.treatmentTiming = this.dataToEdit.treatmentTiming;

        // Flags
        this.isStandAloneTreatment = res.data.data.isStandAloneTreatment;
        // Invert value from DB for toggle switch display (FE)
        this.allowDrugbookVisibility = !res.data.data.isDrugBookDisabled;  
        this.allowAftercareDocsAvailability =  !res.data.data.isAftercareDocDisabled; 
        this.allowEnterUnit = !res.data.data.isEnterUnitDisabled; 
        this.allowReuseTreatment = !res.data.data.isReuseDisabled; 
        this.isHealthTreatment = res.data.data.isHealthTreatment;
        this.shouldRecordDiscardedAmountInDrugbook = res.data.data.shouldRecordDiscardedAmountInDrugbook || false;
        this.history = res.data.data.history || [];
        if (this.dataToEdit.doctorFee) {
          this.dataToSave.doctorFee = this.dataToEdit.doctorFee;
        }
        if (this.dataToEdit.freshConsultationFee) {
          this.dataToSave.freshConsultationFee = this.dataToEdit.freshConsultationFee;
        }
        await this.fetchSettingsByKey();
        await this.fetchSubscriptionTiersList();
        // Price tiers are now included in the treatment type response
        this.priceTiers = res.data.data.priceTiers || [];
        this.initializePriceTierData();
        this.$vs.loading.close();
      } catch (error) {
        this.$vs.loading.close();
      }
    },
    async fetchSubscriptionTiersList() {
      try {
        const res = await this.fetchSubscriptionTiers();
        this.subscriptionTiers = res.data.data.docs || res.data.data || [];
        this.initializePriceTierData();
      } catch (error) {
        console.error('Failed to fetch subscription tiers:', error);
      }
    },
    initializePriceTierData() {
      if (!this.subscriptionTiers || this.subscriptionTiers.length === 0) {
        this.priceTierData = {};
        return;
      }

      const newPriceTierData = {};
      this.subscriptionTiers.forEach(tier => {
        const existingPriceTier = this.priceTiers.find(
          pt => (pt.subscriptionTypeId && pt.subscriptionTypeId._id) === tier._id ||
                 pt.subscriptionTypeId === tier._id ||
                 (pt.subscription_type_id && pt.subscription_type_id._id) === tier._id ||
                 pt.subscription_type_id === tier._id
        );
        newPriceTierData[tier._id] = {
          doctorRate: existingPriceTier ? (existingPriceTier.doctorRate || existingPriceTier.doctor_rate) : null,
          freshclinicsRate: existingPriceTier ? (existingPriceTier.freshclinicsRate || existingPriceTier.freshclinics_rate) : null,
          priceTierId: existingPriceTier ? existingPriceTier._id : null,
        };
      });
      this.priceTierData = newPriceTierData;
    },
    getPriceTierValue(tierId, field) {
      if (this.priceTierData[tierId] && this.priceTierData[tierId][field] != null) {
        return this.priceTierData[tierId][field];
      }
      return null;
    },
    updatePriceTierValue(tierId, field, value) {
      if (!this.priceTierData[tierId]) {
        this.$set(this.priceTierData, tierId, {
          doctorRate: null,
          freshclinicsRate: null,
          priceTierId: null,
        });
      }
      const numValue = value === '' || value === null ? null : parseFloat(value);
      this.$set(this.priceTierData[tierId], field, numValue);
    },
    async submit() {
      const id = this.$route.params.id;
      let isValid = await this.$validator.validate();
      if (isValid) {
        this.$vs.loading();
        if (this.dataToSave.doctorFee === null || this.dataToSave.doctorFee === 0) {
          delete this.dataToSave.doctorFee;
        }
        if (this.dataToSave.freshConsultationFee === null || this.dataToSave.freshConsultationFee === 0) {
          delete this.dataToSave.freshConsultationFee;
        }

        // Prepare price tiers array if field visibility is enabled
        let priceTiers = [];
        if (this.fieldVisibility) {
          for (const tierId in this.priceTierData) {
            const tierData = this.priceTierData[tierId];
            
            // If both values are provided, include in price tiers array
            if (tierData.doctorRate != null && tierData.freshclinicsRate != null) {
              priceTiers.push({
                id: tierData.priceTierId || null,
                subscriptionTypeId: tierId,
                doctorRate: tierData.doctorRate,
                freshclinicsRate: tierData.freshclinicsRate,
              });
            }
          }
        }

        const updatedTreatmentDetails = {
          ...this.dataToSave,
          isDrugBookDisabled: !this.allowDrugbookVisibility,
          isAftercareDocDisabled: !this.allowAftercareDocsAvailability,
          isEnterUnitDisabled: !this.allowEnterUnit,
          isReuseDisabled: !this.allowReuseTreatment,
          isStandAloneTreatment: this.isStandAloneTreatment,
          isHealthTreatment: this.isHealthTreatment,
          shouldRecordDiscardedAmountInDrugbook: this.shouldRecordDiscardedAmountInDrugbook,
          priceTiers: priceTiers,
        };

        try {
          await this.saveTreatmentTiming({ id, data: updatedTreatmentDetails });

          this.$vs.notify({
            title: 'Success',
            text: 'Treatment timing saved successfully',
            color: 'success',
          });
          this.$router.push(`/${this.routeType}/treatment-types`);
          this.$vs.loading.close();
        } catch (err) {
          this.$vs.notify({
            title: (err.data && err.data.title) || 'Error',
            text: (err.data && err.data.message) || 'Failed to save treatment timing',
            color: 'danger',
          });
          this.$vs.loading.close();
        }
      }
    },
    async fetchSettingsByKey() {
      try {
        const res = await this.getSettingsByKey('customConsultationFee');
        if (res.status === 200 && this.dataToEdit && this.dataToEdit._id) {
          this.fieldVisibility = res.data.data.value.some((item) => item.toString() === this.dataToEdit._id.toString());
        }
      } catch (error) {
        console.error(error);
      }
    },
  },
  async created() {
    if (this.$store.state.AppActiveUser.userType === 'superAdmin')
      this.routeType = 'super-admin';
    else this.routeType = 'admin';

    await this.fetchSubscriptionTiersList();
    const id = this.$route.params.id;
    await this.fetchById(id);
  },
};
</script>
<style scoped>
.static-item {
  pointer-events: none;
}
</style>
<style>
#customSidebar > .vs-sidebar {
  max-width: 400px;
  border: none !important;
  padding: 10px;
}
#customSidebar > .vs-sidebar > .vs-sidebar--header {
  background-color: white;
  width: auto;
  padding: 10px 20px;
}
#customSidebar > .vs-sidebar > .vs-sidebar--items {
  overflow: auto;
}
#customSidebar > .vs-sidebar > .vs-sidebar--items > .vs-sidebar--item > a:active, a:visited, a:hover, a {
  color: #626262;
}
</style>
